/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { useFormikContext } from 'formik'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  withPluginColoursStarted,
  withoutPluginColours,
  type PluginEmbedSettings,
  type PluginOptionalColourGroup,
  type PluginPageColour,
} from '@sportninja/common/features/account/apiAccess'
import { ColorField } from '../../../components/FormikField'
import { SettingsCard } from './SettingsCard'
import { SettingSwitch } from './SettingSwitch'

interface OptionalColourGroupProps {
  group: PluginOptionalColourGroup
  /** What the plugin paints for each unset page colour. */
  pageDefaults: Record<PluginPageColour, string>
}

/**
 * One group of optional colours: off leaves them unset (the plugin's default),
 * on starts each from its page colour and shows the swatches.
 */
export const OptionalColourGroup = ({
  group,
  pageDefaults,
}: OptionalColourGroupProps) => {
  const { values, setFieldValue } = useFormikContext<PluginEmbedSettings>()
  const { colours } = values
  const names = group.colours.map(({ name }) => name)
  const isOn = names.some((name) => colours[name] !== undefined)

  return (
    <SettingsCard>
      <SettingSwitch
        label={capitalizeWords(group.caption)}
        hint={
          isOn
            ? group.description
            : `${group.description}. Off: ${group.defaultHint}`
        }
        checked={isOn}
        onChange={(on) =>
          setFieldValue(
            'colours',
            on
              ? withPluginColoursStarted(colours, names, pageDefaults)
              : withoutPluginColours(colours, names)
          )
        }
      />
      {isOn ? (
        <div css={swatchGridStyle}>
          {group.colours.map(({ name, label }) => (
            <ColorField
              key={name}
              name={`colours.${name}`}
              label={capitalizeWords(label)}
            />
          ))}
        </div>
      ) : null}
    </SettingsCard>
  )
}

const swatchGridStyle = css`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
`
